import { GroupListItem } from '@/api/types'
import { useGroupStore } from '@/stores/useGroupStore'

interface GroupListProps {
  onGroupSelect: (group: GroupListItem) => void
  selectedGroup: GroupListItem | null
  isLoading: boolean
  error: string
}

export default function GroupList({
  onGroupSelect,
  selectedGroup,
  isLoading,
  error
}: GroupListProps) {
  const { managingGroups, joinedGroups } = useGroupStore()

  // 显示加载状态
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2">加载中...</p>
        </div>
      </div>
    )
  }

  // 显示错误状态
  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="mt-2 text-red-600">{error}</p>
        </div>
      </div>
    )
  }

  const hasManagingGroups = managingGroups.length > 0
  const hasJoinedGroups = joinedGroups.length > 0

  if (!hasManagingGroups && !hasJoinedGroups) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <p className="mt-2">暂无群组</p>
        </div>
      </div>
    )
  }

  return (
    <div className="py-2">
      {/* 管理的群组 */}
      {hasManagingGroups && (
        <div className="mb-4">
          <div className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b border-gray-100">
            管理员 ({managingGroups.length})
          </div>
          {managingGroups.map((group) => {
            const isSelected = selectedGroup && selectedGroup.id === group.id

            return (
              <div
                key={group.id}
                onClick={() => onGroupSelect(group)}
                className={`
                  px-4 py-3 cursor-pointer transition-colors border-l-2
                  ${isSelected
                    ? 'bg-blue-50 border-l-blue-500'
                    : 'border-l-transparent hover:bg-gray-50'
                  }
                `}
              >
                <div className="flex items-center space-x-3">
                  <div className="relative flex-shrink-0">
                    <img src={group.avatar} alt={group.name} className="w-8 h-8 rounded-full" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {group.name}
                    </p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* 加入的群组 */}
      {hasJoinedGroups && (
        <div>
          <div className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b border-gray-100">
            成员 ({joinedGroups.length})
          </div>
          {joinedGroups.map((group) => {
            const isSelected = selectedGroup && selectedGroup.id === group.id

            return (
              <div
                key={group.id}
                onClick={() => onGroupSelect(group)}
                className={`
                  px-4 py-3 cursor-pointer transition-colors border-l-2
                  ${isSelected
                    ? 'bg-blue-50 border-l-blue-500'
                    : 'border-l-transparent hover:bg-gray-50'
                  }
                `}
              >
                <div className="flex items-center space-x-3">
                  <div className="relative flex-shrink-0">
                    <img src={group.avatar} alt={group.name} className="w-8 h-8 rounded-full" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {group.name}
                    </p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
